import { motion } from "framer-motion";
import { TrendingUp, Coins, Landmark, Scale, FileText, CheckCircle2 } from "lucide-react";
import AuditField from "./AuditField";

export default function BlocRevenus({ data }) {
  const formatCurrency = (val) => {
    const num = Number(val);
    if (isNaN(num) || !val) return "0 €";
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(num);
  };

  const nbParts = 2 + (Number(data.enfantsCount || data.nombreEnfants || 0) * 0.5);

  return (
    <motion.section 
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* 1. FLUX FINANCIERS DU FOYER (IR) */}
      <div className="workspace-card p-6 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm space-y-4">
        <div className="flex items-center gap-2.5 pb-3 border-b border-workspace">
          <TrendingUp className="w-5 h-5 text-secondary" />
          <h2 className="text-lg font-bold text-primary">Flux Financiers & Revenus du Foyer (IR)</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3.5">
          <AuditField label="Revenus Annuels Globaux" value={formatCurrency(data.revenusAnnuels)} />
          <AuditField label="Charges Annuelles Globales" value={formatCurrency(data.chargesAnnuelles)} />
          <AuditField label="Épargne Annuelle (Cash Flow)" value={formatCurrency(data.epargneAnnuelle)} className="text-success-700 font-bold" />
          <AuditField label="Capacité d'Investissement" value={formatCurrency(data.capaciteInvestissement)} className="font-bold text-primary" />
          <AuditField label="Besoin de Liquidité Immédiat" value={formatCurrency(data.besoinLiquidite)} />
          <AuditField label="Taux d'Endettement" value={data.endettementTaux ? `${data.endettementTaux} %` : "Modéré (< 33%)"} />
        </div>
      </div>

      {/* 2. FISCALITÉ GLOBALE : IMPÔT SUR LE REVENU & IFI */}
      <div className="workspace-card p-6 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm space-y-4">
        <div className="flex items-center gap-2.5 pb-3 border-b border-workspace">
          <Scale className="w-5 h-5 text-secondary" />
          <h2 className="text-lg font-bold text-primary">Fiscalité du Foyer (IR & IFI)</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
          <AuditField label="TMI (Tranche Marginale)" value={data.tmi ? `${data.tmi} %` : "30 % (Estimée)"} className="font-bold" />
          <AuditField label="Parts Fiscales Estimées" value={`${nbParts} part(s)`} />
          <AuditField label="Assujetti à l'IFI" value={data.ifiAssujetti ? "Oui" : "Non assujetti (< 1,3 M€)"} />
          <AuditField label="Pression Fiscale Globale" value={data.tmi >= 41 ? "Forte" : "Modérée"} />
        </div>

        {data.fiscaliteCommentaires && (
          <div className="mt-3 p-3.5 rounded-lg bg-surface-muted/30 border border-workspace text-xs text-subtle italic">
            <span className="font-semibold text-muted not-italic block mb-1">Observations Fiscales du Conseiller :</span>
            {data.fiscaliteCommentaires}
          </div>
        )}
      </div>

      {/* 3. PASSERELLE FLUX PERSONNES MORALES (IS) -> FOYER FISCAL (IR) */}
      <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2.5 text-subtle">
          <Coins className="w-4 h-4 text-warning-600 shrink-0" />
          <span>
            <strong>Arbitrage IR vs IS :</strong> Les dividendes des personnes morales remontent au foyer fiscal taxés au PFU (30%), 
            tandis que la trésorerie conservée en holding capitalise à l'IS (taux réduit 15% ou 25%).
          </span>
        </div>
      </div>
    </motion.section>
  );
}
